/**
 * CommandPalette — headless ⌘K overlay: search input + filtered command
 * list. Host owns the open/close boolean, the command list (with
 * state-dependent labels/visibility computed at open time), and i18n
 * labels. No platform imports — same layering as ClipboardScreen.
 *
 * SECURITY: commands are static action names — the palette never renders
 * notification/clip content, so there is nothing here for `publicMode` to
 * mask (see docs/superpowers/specs/2026-07-12-extension-command-palette-design.md §8).
 */
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { trapTabKey } from "../../utils/focusTrap";
import styles from "./CommandPalette.module.css";
import { filterCommands } from "./filterCommands";

export interface PaletteCommand {
  id: string;
  /** Localized, host-provided. */
  label: string;
  /** Localized match alternates. */
  keywords?: string[];
  /** Optional trailing hint (e.g. "30 min"). */
  hint?: string;
  /** Host callback; palette closes after run. */
  run: () => void;
}

export interface CommandPaletteLabels {
  placeholder: string;
  noResults: string;
  ariaLabel: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  commands: PaletteCommand[];
  labels: CommandPaletteLabels;
}

// Stable empty reference — the palette renders nothing while closed (see the
// `if (!open) return null` below), so there's no point re-filtering `commands`
// against a host re-render that happens while the palette isn't shown.
const NO_COMMANDS: PaletteCommand[] = [];

export function CommandPalette({ open, onClose, commands, labels }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Container for the Tab focus trap — the aria-modal boundary.
  const panelRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const filtered = useMemo(
    () => (open ? filterCommands(commands, query) : NO_COMMANDS),
    [open, commands, query]
  );

  // Reopening always starts from a clean slate: empty query, first row
  // active, input focused (no sticky search across opens).
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    inputRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const rowId = (commandId: string) => `${baseId}-option-${commandId}`;
  // `activeIndex` can transiently exceed the filtered list's bounds when the
  // `commands` prop shrinks while the palette is open — clamp before using it
  // so highlight and Enter always agree on the same row.
  const clampedIndex = Math.min(activeIndex, Math.max(0, filtered.length - 1));
  const active = filtered[clampedIndex];

  const runAndClose = (command: PaletteCommand | undefined) => {
    command?.run();
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) setActiveIndex((i) => (i + 1) % filtered.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        break;
      case "Enter":
        e.preventDefault();
        runAndClose(active);
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      case "Tab":
        // The panel declares aria-modal="true", which promises focus stays
        // inside it. Without containment Tab walked straight out to the page
        // behind the backdrop, so the promise was false. The rows are
        // aria-activedescendant-driven and carry no tabindex, so the input is
        // normally the only Tab stop — the trap then collapses to "hold focus
        // on the input", which is the correct modal behaviour.
        trapTabKey(panelRef.current, e);
        break;
      default:
        break;
    }
  };

  return (
    // Backdrop click is a supplementary close affordance; Escape (on the
    // input) is the primary keyboard path — same rationale as FeedbackModal.
    // biome-ignore lint/a11y/noStaticElementInteractions: supplementary backdrop click
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard close is Escape on the input; see comment above
    <div className={styles.backdrop} onClick={onClose}>
      {/* stopPropagation only — clicks inside the panel must not bubble to
          the backdrop's close handler. Real interaction (typing, arrows,
          Enter, Escape) all live on the input; this click guard has no
          keyboard equivalent to pair it with. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: no keyboard action here — see comment above */}
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={labels.ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={active ? rowId(active.id) : undefined}
          aria-label={labels.placeholder}
          placeholder={labels.placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
        <div id={listboxId} role="listbox" aria-label={labels.ariaLabel} className={styles.rows}>
          {filtered.length === 0 ? (
            <div className={styles.noResults}>{labels.noResults}</div>
          ) : (
            filtered.map((command, index) => (
              // Rows are mouse-only affordances layered on the ARIA 1.2
              // combobox pattern — keyboard interaction lives on the input
              // via aria-activedescendant, not real DOM focus on the row
              // (ARIA 1.2 §6.27 forbids interactive descendants inside
              // role="option", so rows carry no tabIndex/keydown of their
              // own; see KbdRow for the sibling precedent in
              // SidePanelNotificationList).
              // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav is via the input's aria-activedescendant, not row focus
              // biome-ignore lint/a11y/useFocusableInteractive: virtual focus via aria-activedescendant, not real DOM focus — see comment above
              <div
                key={command.id}
                id={rowId(command.id)}
                role="option"
                aria-selected={index === clampedIndex}
                className={index === clampedIndex ? `${styles.row} ${styles.active}` : styles.row}
                onClick={() => runAndClose(command)}
              >
                <span className={styles.label}>{command.label}</span>
                {command.hint ? <span className={styles.hint}>{command.hint}</span> : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
